import React from 'react';
import { Box, Table, Typography } from '@mui/material';

/** Tonal colour pairs [background, text] from the theme tokens. */
const TONES = {
  neutral: ['var(--q-subtle, #F1EFEB)', 'var(--q-text-3, #4A463F)'],
  accent: ['var(--q-accent-soft, #ECE8FC)', 'var(--q-accent-text, #3A28B8)'],
  info: ['var(--q-info-soft, #E4EEF8)', 'var(--q-info-text, #1F4E80)'],
  success: ['var(--q-success-soft, #E3F2E8)', 'var(--q-success-text, #17603A)'],
  warning: ['var(--q-warning-soft, #FDEFD6)', 'var(--q-warning-text, #7F4A00)'],
  danger: ['var(--q-danger-soft, #FCE8E2)', 'var(--q-danger-text, #9C2C14)'],
};

/** Display name and tone per tier key. */
export const TIER_META = {
  free: { label: 'Free', tone: 'neutral' },
  pro: { label: 'Pro', tone: 'info' },
  'pro-plus': { label: 'Pro Plus', tone: 'accent' },
  enterprise: { label: 'Enterprise', tone: 'warning' },
};

/** Display name and tone per check status. */
export const STATUS_META = {
  pass: { label: 'Pass', tone: 'success' },
  fail: { label: 'Fail', tone: 'danger' },
  warn: { label: 'Review', tone: 'warning' },
  info: { label: 'Info', tone: 'info' },
  error: { label: 'Error', tone: 'danger' },
};

/**
 * Small tonal pill.
 * @param {object} props - Props.
 * @param {keyof TONES} [props.tone='neutral'] - Tone.
 * @param {React.ReactNode} props.children - Label.
 * @param {object} [props.sx] - Extra styles.
 * @returns {JSX.Element} Chip.
 */
export function ToneChip({ tone = 'neutral', children, sx, ...rest }) {
  const [bg, fg] = TONES[tone] || TONES.neutral;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: 1,
        borderRadius: 'var(--q-radius-pill, 999px)',
        bgcolor: bg,
        color: fg,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        flex: 'none',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/**
 * Tier pill (Free, Pro, Pro Plus, Enterprise).
 * @param {{tier: string}} props - Props.
 * @returns {JSX.Element} Chip.
 */
export function TierChip({ tier }) {
  const meta = TIER_META[tier] || { label: tier || 'Unknown', tone: 'neutral' };
  return <ToneChip tone={meta.tone}>{meta.label}</ToneChip>;
}

/**
 * Check status pill.
 * @param {{status: string}} props - Props.
 * @returns {JSX.Element} Chip.
 */
export function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status, tone: 'neutral' };
  return <ToneChip tone={meta.tone}>{meta.label}</ToneChip>;
}

/**
 * Compact table that scrolls inside its own bordered box (both axes), so a wide table never
 * scrolls the page at 390px.
 * @param {object} props - Props.
 * @param {string} props.label - Accessible name of the scroll region.
 * @param {number|string} [props.maxHeight=520] - Max height before vertical scroll.
 * @param {number} [props.minWidth=640] - Table min width before horizontal scroll.
 * @param {React.ReactNode} props.children - TableHead / TableBody.
 * @returns {JSX.Element} Table.
 */
export function ScrollTable({ label, maxHeight = 520, minWidth = 640, children }) {
  return (
    <Box
      role="region"
      aria-label={label}
      tabIndex={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        maxHeight,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-lg, 10px)',
        bgcolor: 'background.paper',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        '&:focus-visible': { outline: '2px solid var(--q-focus-ring, #4F3BD6)', outlineOffset: 2 },
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          minWidth,
          '& th': {
            bgcolor: 'var(--q-bg-subtle, #FAF9F7)',
            color: 'var(--q-muted, #5E5A54)',
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            borderBottomColor: 'var(--q-border-subtle, #ECE9E4)',
          },
          '& td': { fontSize: 13, borderBottomColor: 'var(--q-row-divider, #F0EDE8)', verticalAlign: 'middle' },
          '& tbody tr:last-of-type td': { borderBottom: 0 },
        }}
      >
        {children}
      </Table>
    </Box>
  );
}

/**
 * Stat tile for the overview grid.
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {React.ReactNode} props.value - Main value.
 * @param {React.ReactNode} [props.hint] - Secondary line.
 * @returns {JSX.Element} Tile.
 */
export function StatTile({ label, value, hint }) {
  return (
    <Box
      sx={{
        p: 2,
        minWidth: 0,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-card, 16px)',
      }}
    >
      <Typography component="div" sx={{ fontSize: 12.5, fontWeight: 600, color: 'var(--q-muted, #5E5A54)' }}>
        {label}
      </Typography>
      <Typography component="div" sx={{ fontSize: 26, fontWeight: 650, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
        {value}
      </Typography>
      {hint && (
        <Typography component="div" sx={{ fontSize: 12.5, color: 'var(--q-faint, #716C65)', mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Monospace id text.
 * @param {{children: React.ReactNode}} props - Props.
 * @returns {JSX.Element} Text.
 */
export function Mono({ children }) {
  return (
    <Box component="span" sx={{ fontFamily: 'var(--q-font-mono, ui-monospace, monospace)', fontSize: 12, color: 'var(--q-text-2, #3D3A35)', overflowWrap: 'anywhere' }}>
      {children}
    </Box>
  );
}

/**
 * Short date ("Nov 2, 2026"), or a fallback.
 * @param {string|null|undefined} iso - ISO timestamp.
 * @param {string} [fallback='Never'] - Text when missing.
 * @returns {string} Date.
 */
export function formatDate(iso, fallback = 'Never') {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Relative age ("Today", "3d ago", "5mo ago"), or a fallback.
 * @param {string|null|undefined} iso - ISO timestamp.
 * @param {number} [now=Date.now()] - Reference time.
 * @param {string} [fallback='Never'] - Text when missing.
 * @returns {string} Age.
 */
export function formatAge(iso, now = Date.now(), fallback = 'Never') {
  if (!iso) return fallback;
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return fallback;
  const days = Math.floor(ms / 86400000);
  if (days <= 0) return 'Today';
  if (days < 60) return `${days}d ago`;
  if (days < 730) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
